// Configure Foreign CF DNS - typed browser client for /api/foreign-dns-runs.
//
// Every shape below is derived from the router (src/routes/foreign-dns-runs.ts)
// and the repo layer (src/services/database/repos/foreign-dns-runs.ts). The
// router is the contract; the design doc is not.
//
// SECURITY: no response this module can receive carries the customer's
// Cloudflare token. The run row exposes `has_token` plus three token
// TIMESTAMPS (cf_token_verified_at / token_purged_at / key_expired_at) - those
// are metadata about a token, never a token and never ciphertext. RUN_SELECT in
// the repo does not contain cf_token_encrypted at all. No type in this file has
// a field that holds a secret, and none should ever be given one.
//
// Timestamps arrive as ISO strings: the repo types them as `Date`, but they
// cross the wire through JSON.stringify.

import { ApiError, api } from '../api/client';

export type { ApiError } from '../api/client';

const BASE = '/api/foreign-dns-runs';

/* ─── Types ─────────────────────────────────────────────────────────────── */

/** Wizard stage names accepted by POST /:id/stage/:stage/run. */
export type FdStage = 'probe' | 'purge' | 'basic' | 'verification' | 'dkim' | 'refresh';

/** Per-domain, per-stage outcome (foreign_dns_run_domains.*_status). */
export type FdStatus = 'pending' | 'running' | 'ok' | 'skipped' | 'failed';

/** Whole-stage outcome (foreign_dns_run_stages.status). */
export type FdStageStatus = 'idle' | 'running' | 'done' | 'failed';

export type FdRunStatus = 'draft' | 'in_progress' | 'completed' | 'cancelled';

export type FdYesNo = 'yes' | 'no';

export type FdCapability = 'unknown' | 'yes' | 'no';

export type FdForwardingMechanism = 'redirect_rules' | 'page_rules' | 'none';

/** A live Cloudflare DNS record, as returned by the Cloudflare API and cached
 *  in foreign_dns_run_domains.live_records. */
export interface FdRecord {
  id: string;
  type: string;
  name: string;
  content: string;
  ttl: number;
  priority?: number;
  proxied?: boolean;
}

export interface FdRun {
  id: string;
  name: string;
  created_by: string;
  created_by_role: string;
  status: FdRunStatus;
  current_step: number;
  /** True iff the server currently holds a verified token for this run. */
  has_token: boolean;
  cf_token_verified_at: string | null;
  token_purged_at: string | null;
  key_expired_at: string | null;
  purge_choice: FdYesNo | null;
  forwarding_choice: FdYesNo | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface FdDomain {
  id: string;
  run_id: string;
  /** Zero-based. This is the ordering contract every paste-spill step maps on. */
  position: number;
  domain: string;
  /**
   * The operator's raw pasted line, before the server normalised it.
   *
   * parseDomainList rewrites SILENTLY - it lower-cases, strips a scheme, a
   * leading `www.`, a trailing dot and everything from the first `/` or `?` -
   * so `www.acme.com` is stored as `acme.com`. Render this beside `domain` or
   * the operator cannot tell why the list they pasted is not the list on
   * screen. Null on rows written before the column existed.
   */
  input_as_typed: string | null;
  zone_root: string;
  record_prefix: string | null;
  zone_id: string | null;
  zone_status: string | null;
  cap_redirect_rules: FdCapability;
  cap_page_rules: FdCapability;
  forwarding_url: string | null;
  forwarding_mechanism: FdForwardingMechanism | null;
  verification_code: string | null;
  dkim_value: string | null;
  purge_status: FdStatus;
  basic_status: FdStatus;
  verification_status: FdStatus;
  dkim_status: FdStatus;
  live_records: FdRecord[] | null;
  live_records_at: string | null;
  /**
   * Advisory or failure text from the last stage that touched this domain.
   *
   * NEVER paint a row from this field. A SUCCESSFUL basic stage legitimately
   * leaves an advisory here (`zone_not_active`), so "last_error is set" does
   * not mean "this domain failed". Read the matching `*_status` instead.
   */
  last_error: string | null;
}

export interface FdStageRow {
  run_id: string;
  stage: FdStage;
  status: FdStageStatus;
  started_at: string | null;
  finished_at: string | null;
  summary: Record<string, unknown> | null;
  error_message: string | null;
}

/** GET /:id */
export interface FdRunDetail {
  run: FdRun;
  domains: FdDomain[];
  stages: FdStageRow[];
}

/** Every stage-enqueueing route reports whether the job actually went on the
 *  queue. `alreadyRunning` means an identical job is mid-flight and could not
 *  be displaced - the caller must NOT tell the operator a new run started. */
export interface FdEnqueueResult {
  enqueued: boolean;
  alreadyRunning: boolean;
}

/** POST /:id/domains */
export interface FdSetDomainsResult extends FdEnqueueResult {
  /** Lines dropped as repeats of an earlier line, verbatim as typed. */
  duplicates: string[];
  /** Lines that did not parse as a hostname, verbatim as typed. */
  invalid: string[];
  /** Domains actually stored. */
  count: number;
}

/** `data` carried alongside a NO_VALID_DOMAINS error (ApiError.data). */
export interface FdNoValidDomainsData {
  duplicates: string[];
  invalid: string[];
}

/** POST /:id/stage/:stage/run */
export interface FdStageRunResult extends FdEnqueueResult {
  stage: FdStage;
}

export interface FdChoicesPatch {
  purge_choice?: FdYesNo;
  forwarding_choice?: FdYesNo;
  /** One URL for every domain, or a single URL broadcast to all of them. */
  forwarding_text?: string;
  /** 1..8. */
  current_step?: number;
}

/** Body accepted by the three record-writing routes. The server coerces a
 *  CNAME to unproxied and forces ttl 1 on anything proxied, whatever is sent. */
export interface FdRecordInput {
  type: 'A' | 'CNAME' | 'TXT' | 'MX';
  name: string;
  content: string;
  ttl?: number;
  priority?: number;
  proxied?: boolean;
}

export interface FdRecordsResult {
  records: FdRecord[];
}

export interface FdRecordWriteResult {
  record: FdRecord;
  records: FdRecord[];
}

/**
 * Every error code this router can return, so callers can branch by name
 * instead of matching on message text.
 *
 * NO_VALID_DOMAINS    400  nothing in the paste parsed; ApiError.data is
 *                          FdNoValidDomainsData
 * DOMAINS_LOCKED      409  a stage past probe has run, the list is frozen
 * KEY_REQUIRED        409  the borrowed token was purged or swept; re-enter it
 * KEY_INVALID         400  Cloudflare rejected the token; nothing was stored
 * RUN_CLOSED          409  run is completed or cancelled, no new key accepted
 * PURGE_NOT_CONSENTED 409  purge stage asked for without a recorded Yes
 * NO_DOMAINS          400  forwarding URLs entered before the domain list
 * FORWARDING_INVALID  400  line count mismatch or an unusable URL
 * DOMAIN_NOT_IN_RUN   404  a bulk write named rows outside this run; no writes
 * ZONE_UNRESOLVED     409  the domain has no Cloudflare zone yet; re-run probe
 * BAD_STAGE           400  unknown stage name in the URL
 * CLOUDFLARE_ERROR    502  upstream call failed; message is already scrubbed
 * NOT_FOUND           404
 * BAD_REQUEST         400
 *
 * The last four do not come from this router but can still reach a caller, so
 * they are in the union: a `switch` on fdErrorCode() must not get a false
 * exhaustiveness guarantee from tsc and then fall through on a real response.
 *
 * INTERNAL_ERROR      500  an unhandled throw (middleware/error-handler.ts)
 * UNAUTHORIZED        401  session expired (auth middleware, before the router)
 * PARSE_ERROR           -  response body was not JSON (client.ts)
 * UNKNOWN               -  error body carried no code, OR a code this union does
 *                          not name - see fdErrorCode below
 */
export type FdErrorCode =
  | 'NO_VALID_DOMAINS'
  | 'DOMAINS_LOCKED'
  | 'KEY_REQUIRED'
  | 'KEY_INVALID'
  | 'RUN_CLOSED'
  | 'PURGE_NOT_CONSENTED'
  | 'NO_DOMAINS'
  | 'FORWARDING_INVALID'
  | 'DOMAIN_NOT_IN_RUN'
  | 'ZONE_UNRESOLVED'
  | 'BAD_STAGE'
  | 'CLOUDFLARE_ERROR'
  | 'NOT_FOUND'
  | 'BAD_REQUEST'
  | 'INTERNAL_ERROR'
  | 'UNAUTHORIZED'
  | 'PARSE_ERROR'
  | 'UNKNOWN';

/** Runtime twin of FdErrorCode. Kept beside the union so adding a member to one
 *  and not the other is visible in a single screenful. */
const FD_ERROR_CODES: ReadonlySet<string> = new Set<FdErrorCode>([
  'NO_VALID_DOMAINS',
  'DOMAINS_LOCKED',
  'KEY_REQUIRED',
  'KEY_INVALID',
  'RUN_CLOSED',
  'PURGE_NOT_CONSENTED',
  'NO_DOMAINS',
  'FORWARDING_INVALID',
  'DOMAIN_NOT_IN_RUN',
  'ZONE_UNRESOLVED',
  'BAD_STAGE',
  'CLOUDFLARE_ERROR',
  'NOT_FOUND',
  'BAD_REQUEST',
  'INTERNAL_ERROR',
  'UNAUTHORIZED',
  'PARSE_ERROR',
  'UNKNOWN',
]);

/* ─── Transport ─────────────────────────────────────────────────────────── */

/**
 * Thin wrapper over the shared client so every call in this module goes through
 * one place. `api` unwraps `{ success: true, data }` and throws `ApiError` with
 * `.code`, `.status` and `.data` on `{ success: false, error }` - the codes
 * above are the whole point of this feature's error handling, so the shared
 * helper is used rather than a local one that would flatten them to a message.
 */
async function req<T>(path: string, init?: RequestInit): Promise<T> {
  return api<T>(`${BASE}${path}`, init);
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return req<T>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

function patch<T>(path: string, body: unknown): Promise<T> {
  return req<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

const enc = encodeURIComponent;

/**
 * The error code of a failed fdApi call, or null when the failure was not an
 * ApiError (a network drop, say).
 *
 * The membership test is the point. `err.code` is a bare string off the wire, so
 * an unchecked cast would hand a caller a value the union does not contain while
 * tsc still believed the union was exhaustive - exactly the false guarantee the
 * union's own comment says it exists to prevent. An unrecognised code is
 * narrowed to 'UNKNOWN', which every caller already has to handle.
 */
export function fdErrorCode(err: unknown): FdErrorCode | null {
  if (!(err instanceof ApiError)) return null;
  return FD_ERROR_CODES.has(err.code) ? (err.code as FdErrorCode) : 'UNKNOWN';
}

/** Operator-facing text for any thrown value. */
export function fdErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Request failed';
}

/**
 * Finish and Cancel share a body: the route purges the token, then answers with
 * `await getForeignDnsRun(id)`, which is typed `ForeignDnsRun | null`.
 *
 * That null is unreachable today - `loadRun` proved the row existed one
 * statement earlier - but the router's type does not promise it, so neither can
 * this. Rather than push a null branch into every caller in Tasks 7 and 8 for a
 * case that means "the run was deleted mid-close", the impossible answer is
 * turned into a loud throw here and the return type stays honestly non-null.
 * The key IS purged when this throws; only the fresh row is missing, so the
 * caller's recovery is to re-list, never to retry the close.
 */
async function closeRun(runId: string, action: 'finish' | 'cancel'): Promise<FdRun> {
  const run = await post<FdRun | null>(`/${enc(runId)}/${action}`);
  if (!run) {
    throw new ApiError(
      'NOT_FOUND',
      `The run disappeared while it was being ${action === 'finish' ? 'finished' : 'cancelled'}. Its Cloudflare key was still purged.`,
      0,
    );
  }
  return run;
}

/* ─── Endpoints ─────────────────────────────────────────────────────────── */

export const fdApi = {
  /** GET / - 100 most recent runs, newest first. No query parameters. */
  listRuns: (): Promise<FdRun[]> =>
    req<{ runs: FdRun[] }>('').then((d) => d.runs),

  /** POST / - name is 1..300 chars after trim. Returns the new draft run. */
  createRun: (name: string): Promise<FdRun> => post<FdRun>('', { name }),

  /** GET /:id - run plus its domains (ordered by position) and stage rows. */
  getRun: (runId: string): Promise<FdRunDetail> => req<FdRunDetail>(`/${enc(runId)}`),

  /**
   * POST /:id/key - verifies the token against Cloudflare and stores it only if
   * it passes. The token never comes back in any later response.
   * Errors: RUN_CLOSED, KEY_INVALID, BAD_REQUEST, NOT_FOUND.
   */
  setKey: (runId: string, token: string): Promise<{ verified: true }> =>
    post<{ verified: true }>(`/${enc(runId)}/key`, { token }),

  /**
   * POST /:id/domains - replaces the whole list and enqueues the probe stage.
   * Re-submitting DISCARDS every zone id, verification code and DKIM value
   * already recorded, which is why the server locks it once a stage past probe
   * has run. Errors: RUN_CLOSED, KEY_REQUIRED, DOMAINS_LOCKED,
   * NO_VALID_DOMAINS (data is FdNoValidDomainsData), BAD_REQUEST, NOT_FOUND.
   */
  setDomains: (runId: string, domainsText: string): Promise<FdSetDomainsResult> =>
    post<FdSetDomainsResult>(`/${enc(runId)}/domains`, { domains_text: domainsText }),

  /**
   * POST /:id/choices - the two yes/no answers, the step pointer, and the
   * forwarding paste. Forwarding is validated before anything is written, so a
   * rejected paste leaves nothing half-applied.
   * Errors: NO_DOMAINS, FORWARDING_INVALID, BAD_REQUEST, NOT_FOUND.
   */
  setChoices: (runId: string, patchBody: FdChoicesPatch): Promise<{ updated: true }> =>
    post<{ updated: true }>(`/${enc(runId)}/choices`, patchBody),

  /**
   * POST /:id/stage/:stage/run - one in-flight job per (run, stage).
   * Check `alreadyRunning` before reporting success.
   * Errors: KEY_REQUIRED, PURGE_NOT_CONSENTED, BAD_STAGE, NOT_FOUND.
   */
  runStage: (runId: string, stage: FdStage): Promise<FdStageRunResult> =>
    post<FdStageRunResult>(`/${enc(runId)}/stage/${enc(stage)}/run`),

  /**
   * PATCH /:id/domains/:domainId - one row, one or both raw values. Stored
   * verbatim; normalisation happens when the stage writes to Cloudflare.
   * Errors: BAD_REQUEST, NOT_FOUND.
   */
  saveDomainField: (
    runId: string,
    domainId: string,
    fields: { verification_code?: string; dkim_value?: string },
  ): Promise<{ updated: true }> =>
    patch<{ updated: true }>(`/${enc(runId)}/domains/${enc(domainId)}`, fields),

  /**
   * PATCH /:id/domains-bulk - one column, many rows, all-or-nothing. 1..5000
   * values. Returns how many rows were written.
   * Errors: DOMAIN_NOT_IN_RUN, BAD_REQUEST, NOT_FOUND.
   */
  saveDomainFieldsBulk: (
    runId: string,
    field: 'verification_code' | 'dkim_value',
    values: { id: string; value: string }[],
  ): Promise<{ updated: number }> =>
    patch<{ updated: number }>(`/${enc(runId)}/domains-bulk`, { field, values }),

  /**
   * GET /:id/domains/:domainId/records - re-reads the zone from Cloudflare and
   * caches it. Never served from live_records.
   * Errors: ZONE_UNRESOLVED, KEY_REQUIRED, CLOUDFLARE_ERROR, NOT_FOUND.
   */
  listRecords: (runId: string, domainId: string): Promise<FdRecord[]> =>
    req<FdRecordsResult>(`/${enc(runId)}/domains/${enc(domainId)}/records`).then(
      (d) => d.records,
    ),

  /** POST /:id/domains/:domainId/records - returns the created record plus a
   *  fresh listing of the zone. */
  addRecord: (
    runId: string,
    domainId: string,
    record: FdRecordInput,
  ): Promise<FdRecordWriteResult> =>
    post<FdRecordWriteResult>(`/${enc(runId)}/domains/${enc(domainId)}/records`, record),

  /** PATCH /:id/domains/:domainId/records/:recordId - full replacement body,
   *  not a partial patch. Returns the record plus a fresh listing. */
  editRecord: (
    runId: string,
    domainId: string,
    recordId: string,
    record: FdRecordInput,
  ): Promise<FdRecordWriteResult> =>
    patch<FdRecordWriteResult>(
      `/${enc(runId)}/domains/${enc(domainId)}/records/${enc(recordId)}`,
      record,
    ),

  /** DELETE /:id/domains/:domainId/records/:recordId - returns a fresh listing. */
  deleteRecord: (runId: string, domainId: string, recordId: string): Promise<FdRecord[]> =>
    req<FdRecordsResult>(
      `/${enc(runId)}/domains/${enc(domainId)}/records/${enc(recordId)}`,
      { method: 'DELETE' },
    ).then((d) => d.records),

  /** POST /:id/finish - purges the borrowed key and completes the run. Final:
   *  a completed run will not accept a key again. */
  finishRun: (runId: string): Promise<FdRun> => closeRun(runId, 'finish'),

  /** POST /:id/cancel - purges the borrowed key and cancels the run. Also
   *  final. Every typed-in value survives; only the key is destroyed. */
  cancelRun: (runId: string): Promise<FdRun> => closeRun(runId, 'cancel'),
};
